import React, { useState, useEffect } from 'react';
import { cn, getAuthCallbackUrl } from "../lib/utils.mjs";
import { Button } from "./ui/button";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { wixClient } from '../config/wix.mjs';
import { useAuth } from '../context/AuthContext';
import { trackAuthSuccess } from '../lib/authTelemetry';
import { mintSnapshotEntitlements } from '../lib/snapshotEntitlements';
import { fetchServerSession, persistLocalWixTokens, startServerSession } from '../lib/sessionAuth';

const OAUTH_STORAGE_KEY = 'oauthRedirectData';
const FREE_ACCESS_REDIRECT_DELAY_MS = 8000;
const FREE_ACCESS_REDIRECT_DELAY_SECONDS = FREE_ACCESS_REDIRECT_DELAY_MS / 1000;
const FREE_ACCESS_REDIRECT_URL = new URL(
  '/',
  (
    process.env.REACT_APP_CANONICAL_APP_ORIGIN || 'https://platform.quantchecklabs.com'
  ).trim() || 'https://platform.quantchecklabs.com'
).toString();
const FREE_ACCESS_PRICING_URL =
  'https://www.quantchecklabs.com/pricing?utm_source=platform&utm_medium=login_gate&utm_campaign=free_to_premium';
const legacyWixBaseUrl = (
  process.env.REACT_APP_LEGACY_WIX_BASE_URL || 'https://legacy.quantchecklabs.com'
).replace(/\/$/, '');

const getParam = (url, camelCaseKey, snakeCaseKey) =>
  url.searchParams.get(camelCaseKey) || url.searchParams.get(snakeCaseKey);

const normalizeRedirectPath = (value) => {
  if (!value) return '/';
  const trimmed = String(value).trim();
  if (!trimmed.startsWith('/')) return '/';
  if (trimmed.startsWith('//')) return '/';
  return trimmed;
};

const getLoginRedirectPath = () => {
  if (typeof window === 'undefined') return '/';
  const params = new URLSearchParams(window.location.search);
  return normalizeRedirectPath(params.get('redirect'));
};

const getLoginPageStatus = (search) => {
  const params = new URLSearchParams(search);
  if (params.get('reset') === 'success') {
    return {
      type: 'success',
      message: 'Password updated. Sign in with your new password.',
    };
  }

  return { type: '', message: '' };
};

const buildForgotPasswordPath = (email, required = false) => {
  const params = new URLSearchParams();
  const trimmedEmail = (email || '').trim();

  if (trimmedEmail) {
    params.set('email', trimmedEmail);
  }

  if (required) {
    params.set('required', '1');
  }

  const query = params.toString();
  return query ? `/forgot-password?${query}` : '/forgot-password';
};

const buildLegacyGoogleLoginUrl = (value) => {
  const authUrl = new URL(value);
  const headlessExternalUrls = authUrl.searchParams.get('headlessExternalUrls');
  const clientId = getParam(authUrl, 'clientId', 'client_id');
  const responseType = getParam(authUrl, 'responseType', 'response_type');
  const redirectUri = getParam(authUrl, 'redirectUri', 'redirect_uri');
  const scope = authUrl.searchParams.get('scope');
  const state = authUrl.searchParams.get('state');
  const responseMode = getParam(authUrl, 'responseMode', 'response_mode');
  const codeChallenge = getParam(authUrl, 'codeChallenge', 'code_challenge');
  const codeChallengeMethod = getParam(authUrl, 'codeChallengeMethod', 'code_challenge_method');

  if (
    !headlessExternalUrls ||
    !clientId ||
    !responseType ||
    !redirectUri ||
    !scope ||
    !state ||
    !responseMode ||
    !codeChallenge ||
    !codeChallengeMethod
  ) {
    return value;
  }

  const legacyAuthorizeUrl = new URL(`${legacyWixBaseUrl}/_api/oauth2/authorize`);
  legacyAuthorizeUrl.searchParams.set('client_id', clientId);
  legacyAuthorizeUrl.searchParams.set('response_type', responseType);
  legacyAuthorizeUrl.searchParams.set('redirect_uri', redirectUri);
  legacyAuthorizeUrl.searchParams.set('scope', scope);
  legacyAuthorizeUrl.searchParams.set('state', state);
  legacyAuthorizeUrl.searchParams.set('response_mode', responseMode);
  legacyAuthorizeUrl.searchParams.set('code_challenge', codeChallenge);
  legacyAuthorizeUrl.searchParams.set('code_challenge_method', codeChallengeMethod);

  const legacyLoginUrl = new URL(`${legacyWixBaseUrl}/__auth/loginv2`);
  legacyLoginUrl.searchParams.set('showDialog', 'MemberLoginDialogV2');
  legacyLoginUrl.searchParams.set('headlessExternalUrls', headlessExternalUrls);
  legacyLoginUrl.searchParams.set('authorization_url', legacyAuthorizeUrl.toString());
  return legacyLoginUrl.toString();
};

const sanitizeGoogleAuthUrl = (value) => {
  try {
    const url = new URL(value);
    url.searchParams.delete('sessionToken');
    return buildLegacyGoogleLoginUrl(url.toString());
  } catch {
    return value;
  }
};

export function LoginForm({
  className = "w-full",
  ...props
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    userEmail,
    userName,
    authReady,
    setUserInfo,
    setIsPremium,
    logout: clearAuthState
  } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    verificationCode: '',
    firstName: '',
    lastName: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [member, setMember] = useState(null);
  const [memberEmail, setMemberEmail] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [isSignUp, setIsSignUp] = useState(false);
  const displayName = member?.profile?.nickname || userName || 'User';
  const resolvedMemberEmail = memberEmail || userEmail || '';

  useEffect(() => {
    if (!member || isCheckingAccess || hasAccess) {
      return undefined;
    }

    const redirectTimer = window.setTimeout(() => {
      window.location.assign(FREE_ACCESS_REDIRECT_URL);
    }, FREE_ACCESS_REDIRECT_DELAY_MS);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [member, isCheckingAccess, hasAccess]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextEmail = (params.get('email') || '').trim();

    if (!nextEmail) {
      return;
    }

    setFormData((prev) => (prev.email === nextEmail ? prev : { ...prev, email: nextEmail }));
  }, [location.search]);

  const getAccessToken = async (sessionToken) => {
    // Implement multiple retries with increasing delays.
    const MAX_RETRIES = 10;
    let lastError = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const delayMs = attempt === 0 ? 0 : 2000 + (attempt - 1) * 2000;
        if (delayMs > 0) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }

        const tokens = await wixClient.auth.getMemberTokensForDirectLogin(sessionToken);
        if (tokens?.accessToken?.value) {
          wixClient.auth.setTokens(tokens);
          return tokens;
        }
        throw new Error('Invalid token structure received');
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Failed to get tokens after multiple attempts');
  };

  const getMemberEmail = async (existingAccessToken = null) => {
    try {
      // Add delay before starting to let session token propagate
      await new Promise(resolve => setTimeout(resolve, 500));

      // Use existing access token if provided
      if (!existingAccessToken) {
        console.error('No access token provided to getMemberEmail');
        throw new Error('Access token is required');
      }

      // Add delay before member fetch
      await new Promise(resolve => setTimeout(resolve, 500));

      // Fetch the current member with retries
      const MAX_RETRIES = 3;
      let memberData = null;
      let lastError = null;

      for (let i = 0; i < MAX_RETRIES; i++) {
        try {
          const currentMemberResponse = await wixClient.members.getCurrentMember();
          memberData = currentMemberResponse.member;
          if (memberData) break;
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (err) {
          lastError = err;
          if (i === MAX_RETRIES - 1) throw err;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (!memberData) {
        throw lastError || new Error('Failed to get member data after retries');
      }

      const memberId = memberData.contactId || memberData._id;
      if (!memberId) {
        throw new Error('Member identifier not found in member data');
      }

      // Now fetch full member details with retries
      let fullMember = null;
      for (let i = 0; i < MAX_RETRIES; i++) {
        try {
          fullMember = await wixClient.members.getMember(memberId, { fieldsets: "EXTENDED" });
          if (fullMember) break;
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (err) {
          if (i === MAX_RETRIES - 1) {
            fullMember = memberData;
          } else {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      const email = fullMember.loginEmail || memberData.loginEmail;
      
      if (!email) {
        throw new Error('No email found in member data');
      }

      setMemberEmail(email);
      return email;
      
    } catch (error) {
      throw error;
    }
  };

  const checkPremiumAccess = async () => {
    try {
      setIsCheckingAccess(true);
      const session = await fetchServerSession({ refresh: true });
      const isPremiumUser = Boolean(session?.isPremium);
      setHasAccess(isPremiumUser);
      setIsPremium(isPremiumUser);
      if (isPremiumUser) {
        // Use window.location.href for navigation after setting all states
        setTimeout(() => {
          window.location.href = '/';
        }, 1000); // Small delay to ensure states are set
      }
    } catch (error) {
      console.error('Error checking premium access:', error);
      setHasAccess(false);
      setIsPremium(false);
    } finally {
      setIsCheckingAccess(false);
    }
  };

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        if (!authReady) {
          return;
        }

        const contextEmail = (userEmail || '').trim().toLowerCase();
        if (contextEmail) {
          const displayName = (userName || 'User').trim() || 'User';
          setMember({ profile: { nickname: displayName } });
          setMemberEmail(contextEmail);
          setStatus({
            type: 'success',
            message: `Already logged in as ${displayName}`
          });
          await checkPremiumAccess();
          return;
        }

        const isLoggedIn = await wixClient.auth.loggedIn();
        
        if (isLoggedIn) {
          try {
            const currentMember = await wixClient.members.getCurrentMember();
            setMember(currentMember.member);
            const email =
              currentMember?.member?.loginEmail ||
              currentMember?.member?.profile?.email ||
              currentMember?.member?.contact?.emails?.[0]?.email ||
              currentMember?.member?.contact?.emails?.[0] ||
              null;
            if (email) {
              setMemberEmail(email);
            }
            setStatus({
              type: 'success',
              message: `Already logged in as ${currentMember.member.profile.nickname || 'User'}`
            });
            // Check premium access when user is logged in
            if (email) {
              await checkPremiumAccess();
            } else {
              console.warn('Logged-in member email not found; defaulting to free access state.');
              setHasAccess(false);
              setIsPremium(false);
              setIsCheckingAccess(false);
            }
          } catch (memberError) {
            console.error('Error fetching member details:', memberError);
            // If we can't get member details but we're logged in, show a generic success
            setStatus({
              type: 'success',
              message: 'Successfully logged in'
            });
            setHasAccess(false);
            setIsPremium(false);
            setIsCheckingAccess(false);
          }
        } else {
          setMember(null);
          setMemberEmail(null);
          setStatus(getLoginPageStatus(location.search));
          setHasAccess(false);
          setIsPremium(false);
          setIsCheckingAccess(false);
        }
      } catch (error) {
        console.error('Error checking login status:', error);
        setStatus({
          type: 'error',
          message: 'Error checking login status'
        });
        setHasAccess(false);
        setIsPremium(false);
        setIsCheckingAccess(false);
      }
    };

    checkLoginStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, userEmail, userName, navigate, location.search]);

  const handleVerification = async () => {
    try {
      setIsLoading(true);
      setStatus({ type: '', message: '' });

      const response = await wixClient.auth.processVerification({
        verificationCode: formData.verificationCode
      });

      if (response.loginState === 'SUCCESS') {
        const currentMember = await wixClient.members.getCurrentMember();
        setMember(currentMember.member);
        setShowVerification(false);
        setStatus({
          type: 'success',
          message: 'Successfully verified and logged in!'
        });
      } else {
        setStatus({
          type: 'error',
          message: 'Verification failed. Please try again.'
        });
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus({
        type: 'error',
        message: 'Failed to verify code. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const login = async () => {
    try {
      setIsLoading(true);
      setIsCheckingAccess(true);
      setStatus({ type: '', message: '' });

      const response = await wixClient.auth.login({
        email: formData.email,
        password: formData.password
      });

      if (response.loginState === 'SUCCESS') {
        if (response.data && response.data.sessionToken) {
          const sessionToken = response.data.sessionToken;
          
          // Get access token once
          const tokenBundle = await getAccessToken(sessionToken);
          if (!tokenBundle?.accessToken?.value) {
            throw new Error('Failed to get access token bundle');
          }
          persistLocalWixTokens(tokenBundle);

          // Extract the token string from the token object
          const tokenString = tokenBundle.accessToken.value;
          if (!tokenString) {
            throw new Error('No token string found in access token');
          }

          // Then get the member email using the existing access token
          const email = await getMemberEmail(tokenBundle);
          if (!email) {
            throw new Error('Failed to get member email');
          }

          // Get current member data
          const currentMember = await wixClient.members.getCurrentMember();
          setMember(currentMember.member);
          
          const name = currentMember.member.profile.nickname || null;
          
          let resolvedEmail = email;
          let resolvedName = name;
          let resolvedToken = tokenString;
          let resolvedPremium = null;

          const sessionState = await startServerSession(tokenBundle).catch(() => null);
          if (sessionState?.accessToken) {
            resolvedEmail = sessionState.userEmail || email;
            resolvedName = sessionState.userName || name;
            resolvedToken = sessionState.accessToken;
            resolvedPremium = sessionState.isPremium;
            setIsPremium(sessionState.isPremium);
          }

          // Set user info in context with a validated server session token when available.
          setUserInfo(resolvedEmail, resolvedName, resolvedToken);

          try {
            await mintSnapshotEntitlements(resolvedToken);
          } catch (snapshotError) {
            console.warn('Failed to mint snapshot entitlements on login:', snapshotError);
          }

          trackAuthSuccess({
            eventType: 'login',
            email: resolvedEmail,
            method: 'email_password',
            source: 'login_form',
            accessToken: resolvedToken,
            isPremium: Boolean(resolvedPremium)
          });

          // Prime UI from server session when present, then always verify access status.
          if (typeof resolvedPremium === 'boolean') {
            setHasAccess(resolvedPremium);
            setIsPremium(resolvedPremium);
            setIsCheckingAccess(false);
            if (resolvedPremium) {
              setTimeout(() => {
                window.location.href = '/';
              }, 1000);
            }
          } else {
            await checkPremiumAccess();
          }
          
          setStatus({
            type: 'success',
            message: `Successfully logged in as ${name || 'User'}`
          });
        } else {
          throw new Error('No session token received');
        }
      } else if (response.loginState === 'FAILURE') {
        let errorMessage = 'Login failed. Please check your credentials.';
        if (response.errorCode === 'invalidEmail') {
          errorMessage = 'Invalid email address.';
        } else if (response.errorCode === 'invalidPassword') {
          errorMessage = 'Invalid password.';
        } else if (response.errorCode === 'resetPassword') {
          navigate(buildForgotPasswordPath(formData.email, true));
          return;
        }
        setStatus({
          type: 'error',
          message: errorMessage
        });
      } else if (response.loginState === 'EMAIL_VERIFICATION_REQUIRED') {
        setShowVerification(true);
        setStatus({
          type: 'info',
          message: 'Please enter the verification code sent to your email.'
        });
      } else if (response.loginState === 'OWNER_APPROVAL_REQUIRED') {
        setStatus({
          type: 'info',
          message: 'Your membership is pending approval from the site owner.'
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      setIsCheckingAccess(false);
      setStatus({
        type: 'error',
        message: error.message || 'Failed to login. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async () => {
    try {
      setIsLoading(true);
      setIsCheckingAccess(true);
      setStatus({ type: '', message: '' });

      // Register the member
      const { approvalStatus } = await wixClient.auth.register({
        email: formData.email,
        password: formData.password,
        profile: {
          firstName: formData.firstName,
          lastName: formData.lastName,
        },
      });

      if (approvalStatus === 'PENDING') {
        setStatus({
          type: 'info',
          message: 'Registration successful. Please wait for admin approval.'
        });
        return;
      }

      // If registration is successful, attempt to log in
      const loginResponse = await wixClient.auth.login({
        email: formData.email,
        password: formData.password
      });

      if (loginResponse.loginState === 'SUCCESS') {
        const sessionToken = loginResponse.data.sessionToken;
        
        // First get the access token
        const tokens = await wixClient.auth.getMemberTokensForDirectLogin(sessionToken);
        if (!tokens) {
          throw new Error('Failed to get tokens');
        }
        persistLocalWixTokens(tokens);

        // Set the tokens in the wixClient
        wixClient.auth.setTokens(tokens);

        // Get current member data
        const currentMember = await wixClient.members.getCurrentMember();
        if (!currentMember || !currentMember.member) {
          throw new Error('Failed to get member data');
        }

        const email = currentMember.member.loginEmail;
        const name = currentMember.member.profile?.nickname || 
                    `${formData.firstName} ${formData.lastName}`.trim();

        // Set user info in context with the token
        const accessToken = tokens.accessToken?.value || tokens.access_token || null;
        let resolvedEmail = email;
        let resolvedName = name;
        let resolvedToken = accessToken;
        let resolvedPremium = null;

        const sessionState = await startServerSession(tokens).catch(() => null);
        if (sessionState?.accessToken) {
          resolvedEmail = sessionState.userEmail || email;
          resolvedName = sessionState.userName || name;
          resolvedToken = sessionState.accessToken;
          resolvedPremium = sessionState.isPremium;
          setIsPremium(sessionState.isPremium);
        }

        setUserInfo(resolvedEmail, resolvedName, resolvedToken);

        try {
          await mintSnapshotEntitlements(resolvedToken || null);
        } catch (snapshotError) {
          console.warn('Failed to mint snapshot entitlements on signup:', snapshotError);
        }

        trackAuthSuccess({
          eventType: 'signup',
          email: resolvedEmail,
          method: 'email_password',
          source: 'signup_form',
          accessToken: resolvedToken || null,
          isPremium: Boolean(resolvedPremium)
        });

        // Prime UI from server session when present, then always verify access status.
        if (typeof resolvedPremium === 'boolean') {
          setHasAccess(resolvedPremium);
          setIsPremium(resolvedPremium);
          setIsCheckingAccess(false);
          if (resolvedPremium) {
            setTimeout(() => {
              window.location.href = '/';
            }, 1000);
          }
        } else {
          await checkPremiumAccess();
        }
        
        setStatus({
          type: 'success',
          message: `Successfully registered and logged in as ${name || 'User'}`
        });

        // Wait for a short time to ensure all states are set
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Navigate to home page
        navigate('/', { replace: true });
      } else if (loginResponse.loginState === 'EMAIL_VERIFICATION_REQUIRED') {
        setShowVerification(true);
        setStatus({
          type: 'info',
          message: 'Please enter the verification code sent to your email.'
        });
      } else {
        throw new Error(`Login failed: ${loginResponse.loginState}`);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setIsCheckingAccess(false);
      setStatus({
        type: 'error',
        message: error.message || 'Failed to sign up. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (showVerification) {
      if (!formData.verificationCode) {
        setStatus({
          type: 'error',
          message: 'Please enter the verification code.'
        });
        return;
      }
      await handleVerification();
      return;
    }

    if (!formData.email || !formData.password) {
      setStatus({
        type: 'error',
        message: 'Please enter both email and password.'
      });
      return;
    }

    if (isSignUp) {
      if (!formData.firstName || !formData.lastName) {
        setStatus({
          type: 'error',
          message: 'Please enter your first and last name.'
        });
        return;
      }
      await signup();
    } else {
      await login();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogout = async () => {
    try {
      const { logoutUrl } = await wixClient.auth.logout();
      clearAuthState();
      setMember(null);
      setMemberEmail(null);
      setHasAccess(false);
      // Check if logoutUrl is a string and use a default if it's not
      const redirectUrl = typeof logoutUrl === 'string' ? logoutUrl : '/login';
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Logout error:', error);
      setStatus({
        type: 'error',
        message: 'Failed to logout. Please try again.'
      });
      // In case of error, redirect to login page
      navigate('/login');
    }
  };

  const loginWithGoogle = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      localStorage.removeItem(OAUTH_STORAGE_KEY);
      sessionStorage.removeItem(OAUTH_STORAGE_KEY);

      const redirectUri = getAuthCallbackUrl();
      const redirectPath = getLoginRedirectPath();
      const oauthData = await wixClient.auth.generateOAuthData(redirectUri, redirectPath);
      const dataToStore = {
        ...oauthData,
        timestamp: Date.now(),
      };

      localStorage.setItem(OAUTH_STORAGE_KEY, JSON.stringify(dataToStore));
      sessionStorage.setItem(OAUTH_STORAGE_KEY, JSON.stringify(dataToStore));

      const authUrlResponse = await wixClient.auth.getAuthUrl(oauthData);

      // Handle different response formats
      let authUrl;
      if (typeof authUrlResponse === 'string') {
        authUrl = authUrlResponse;
      } else if (authUrlResponse && typeof authUrlResponse === 'object') {
        authUrl = authUrlResponse.authUrl;
      }
      
      // Make sure we have a valid URL string
      if (!authUrl || typeof authUrl !== 'string') {
        throw new Error('Invalid auth URL received from Wix');
      }

      window.location.assign(sanitizeGoogleAuthUrl(authUrl));
    } catch (error) {
      console.error('Google login error:', error);
      setStatus({
        type: 'error',
        message: 'Failed to initiate Google login. Please try again.'
      });
      setIsLoading(false);
    }
  };

  const forgotPasswordPath = buildForgotPasswordPath(formData.email);

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Login</CardTitle>
          <CardDescription>
            {member ? 'You are logged in' : 'Enter your email below to login to your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {member ? (
            <div className="mx-auto flex w-full max-w-[760px] flex-col gap-4">
              <div className="rounded-[14px] border border-[var(--qcl-border)] bg-white p-4 shadow-[var(--qcl-shadow)] sm:p-5">
                <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--qcl-text-muted)]">
                  Account
                </div>
                <div className="mt-1 text-[20px] font-bold tracking-[-0.02em] text-[var(--qcl-text)]">
                  Logged in as {displayName}
                </div>
                {resolvedMemberEmail ? (
                  <div className="mt-1 text-[14px] text-[var(--qcl-text-dim)]">{resolvedMemberEmail}</div>
                ) : null}
              </div>

              <div className="rounded-[14px] border border-[var(--qcl-border)] bg-white p-5 shadow-[var(--qcl-shadow)] sm:p-6">
                {isCheckingAccess || isLoading ? (
                  <div className="flex flex-col gap-2 text-left">
                    <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--qcl-text-muted)]">
                      Access Check
                    </div>
                    <span className="text-[18px] font-bold tracking-[-0.02em] text-[var(--qcl-text)]">
                      Checking your access level...
                    </span>
                    <span className="text-[14px] text-[var(--qcl-text-dim)]">
                      We are confirming whether this account has Premium access.
                    </span>
                  </div>
                ) : hasAccess ? (
                  <div className="flex flex-col gap-2 text-left">
                    <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--qcl-text-muted)]">
                      Premium Access
                    </div>
                    <span className="text-[18px] font-bold tracking-[-0.02em] text-[var(--qcl-text)]">
                      Premium access confirmed.
                    </span>
                    <span className="text-[14px] text-[var(--qcl-text-dim)]">
                      Redirecting you into the platform now.
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-5 text-left">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="max-w-[540px]">
                        <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--qcl-text-muted)]">
                          Free Access
                        </div>
                        <div className="mt-1 text-[22px] font-bold tracking-[-0.03em] text-[var(--qcl-text)]">
                          Your account can view delayed data.
                        </div>
                        <p className="mt-2 text-[14px] leading-6 text-[var(--qcl-text-dim)]">
                          Continue to the app for delayed snapshots, or review Pricing to unlock the latest full rankings and premium strategy details.
                        </p>
                      </div>
                      <div className="rounded-[12px] border border-[var(--qcl-border)] bg-[var(--qcl-bg)] px-4 py-3 sm:min-w-[160px]">
                        <div className="text-[10px] font-bold uppercase tracking-[1px] text-[var(--qcl-text-muted)]">
                          Auto Redirect
                        </div>
                        <div className="mt-1 text-[18px] font-bold tracking-[-0.02em] text-[var(--qcl-text)]">
                          {FREE_ACCESS_REDIRECT_DELAY_SECONDS}s
                        </div>
                        <div className="mt-1 text-[12px] text-[var(--qcl-text-dim)]">
                          We&apos;ll send you back to the app if you do nothing.
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <a
                        href={FREE_ACCESS_REDIRECT_URL}
                        className="inline-flex min-h-[44px] items-center justify-center rounded-[10px] bg-[var(--qcl-accent)] px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-[var(--qcl-accent-hover)]"
                      >
                        Continue to App
                      </a>
                      <a
                        href={FREE_ACCESS_PRICING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-[44px] items-center justify-center rounded-[10px] border border-[var(--qcl-border)] px-4 py-2 text-[13px] font-semibold text-[var(--qcl-text)] transition hover:bg-[var(--qcl-bg)]"
                      >
                        View Pricing
                      </a>
                    </div>

                    <div className="border-t border-[var(--qcl-border)] pt-4 text-[13px] text-[var(--qcl-text-dim)]">
                      Need a different account?{' '}
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="font-semibold text-[var(--qcl-accent)] transition hover:text-[var(--qcl-accent-hover)]"
                      >
                        Log out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {isCheckingAccess || hasAccess ? (
                <Button
                  onClick={handleLogout}
                  variant="outline"
                >
                  Logout
                </Button>
              ) : null}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-6">
                {status.message && (
                  <div className={cn(
                    "p-3 rounded-md text-sm",
                    {
                      "bg-green-100 text-green-800": status.type === 'success',
                      "bg-red-100 text-red-800": status.type === 'error',
                      "bg-blue-100 text-blue-800": status.type === 'info'
                    }
                  )}>
                    {status.message}
                  </div>
                )}
                {showVerification ? (
                  <div className="grid gap-2">
                    <Label htmlFor="verificationCode">Verification Code</Label>
                    <Input
                      id="verificationCode"
                      name="verificationCode"
                      type="text"
                      placeholder="Enter verification code"
                      value={formData.verificationCode}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    />
                  </div>
                ) : (
                  <>
                    {isSignUp && (
                      <>
                        <div className="grid gap-2">
                          <Label htmlFor="firstName">First Name</Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            type="text"
                            placeholder="John"
                            value={formData.firstName}
                            onChange={handleChange}
                            required={isSignUp}
                            disabled={isLoading}
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="lastName">Last Name</Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            type="text"
                            placeholder="Doe"
                            value={formData.lastName}
                            onChange={handleChange}
                            required={isSignUp}
                            disabled={isLoading}
                          />
                        </div>
                      </>
                    )}
                    <div className="grid gap-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="m@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                      />
                    </div>
                    <div className="grid gap-2">
                      <div className="flex items-center">
                        <Label htmlFor="password">Password</Label>
                        {!isSignUp && (
                          <Link
                            to={forgotPasswordPath}
                            className="ml-auto text-sm text-blue-600 hover:text-blue-800"
                          >
                            Forgot password?
                          </Link>
                        )}
                      </div>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </>
                )}
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading 
                    ? "Processing..." 
                    : showVerification 
                      ? "Verify Code"
                      : isSignUp
                        ? "Sign Up"
                        : "Login with Email"
                  }
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  disabled={isLoading}
                  onClick={loginWithGoogle}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </Button>
                <div className="mt-4 text-center text-sm">
                  {isSignUp ? (
                    <>
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setIsSignUp(false)}
                        className="text-blue-600 hover:text-blue-800 underline underline-offset-4"
                      >
                        Log in
                      </button>
                    </>
                  ) : (
                    <>
                      Don&apos;t have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setIsSignUp(true)}
                        className="text-blue-600 hover:text-blue-800 underline underline-offset-4"
                      >
                        Sign up
                      </button>
                    </>
                  )}
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 
